import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, Grid, LinearProgress, IconButton, Switch, Tooltip } from '@mui/material';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import SettingsIcon from '@mui/icons-material/Settings';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

const SensorCard = ({ label, value, unit, icon, color, optimal }: any) => {
  const theme = useTheme();
  return (
    <Box 
      sx={{ 
        p: 2, 
        borderRadius: 2, 
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="start">
        <Box 
          sx={{ 
            p: 1, 
            borderRadius: '50%', 
            bgcolor: `${color}20`, 
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Tooltip title="Settings">
          <IconButton size="small">
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box mt={2}>
        <Typography variant="h4" fontWeight="bold">
          {value}<Typography component="span" variant="caption" color="text.secondary">{unit}</Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <LinearProgress 
            variant="determinate" 
            value={75} 
            sx={{ 
              flexGrow: 1, 
              height: 6, 
              borderRadius: 3,
              bgcolor: `${color}20`,
              '& .MuiLinearProgress-bar': { bgcolor: color }
            }} 
          />
          <Typography variant="caption" fontWeight="bold" color={optimal ? 'success.main' : 'warning.main'}>
            {optimal ? 'Optimal' : 'Check'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SmartIrrigation: React.FC = () => {
  const theme = useTheme();
  const [autoMode, setAutoMode] = React.useState(true);

  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: theme.shadows[2] }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <WaterDropIcon color="primary" />
              Smart Irrigation Control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Zone A - Greenhouse 1
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} bgcolor={theme.palette.action.hover} p={0.5} borderRadius={2}>
            <Typography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>AUTO</Typography>
            <Switch 
              size="small" 
              checked={autoMode} 
              onChange={(e) => setAutoMode(e.target.checked)} 
              color="primary"
            />
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SensorCard 
              label="Soil Moisture" 
              value="68" 
              unit="%" 
              icon={<OpacityIcon />} 
              color={theme.palette.primary.main}
              optimal={true}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SensorCard 
              label="Soil Temp" 
              value="22.4" 
              unit="°C" 
              icon={<ThermostatIcon />} 
              color={theme.palette.warning.main}
              optimal={true}
            />
          </Grid>
        </Grid>

        <Box mt={3} p={2} borderRadius={2} bgcolor={theme.palette.primary.main} color="white">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">Next Scheduled Watering</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Today, 18:00 (45 mins)</Typography>
            </Box>
            <IconButton sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <PowerSettingsNewIcon />
            </IconButton>
          </Box>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
            System efficiency: 98% • Water saved this week: 450L
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SmartIrrigation;
